import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "kirchhero_cart_v1";
const SHOPIFY_DOMAIN = "kreakraft.myshopify.com";

function loadCart() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persist
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.variantId === item.variantId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const updateQuantity = useCallback((variantId, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.variantId === variantId ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => prev.filter((i) => i.variantId !== variantId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.quantity * Number(i.price || 0), 0);
    return { count, subtotal };
  }, [items]);

  /**
   * Build a Shopify cart-permalink and redirect to gebrandetes Shopify-Checkout.
   * Permalink format: https://{shop}.myshopify.com/cart/VARIANT_ID:QTY,VARIANT_ID:QTY
   * No API token / auth needed.
   */
  const checkout = useCallback(() => {
    if (items.length === 0) return;
    const segments = items.map((i) => `${i.variantId}:${i.quantity}`).join(",");
    const url = `https://${SHOPIFY_DOMAIN}/cart/${segments}`;
    window.location.href = url;
  }, [items]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const value = useMemo(
    () => ({
      items,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      checkout,
      drawerOpen,
      openDrawer,
      closeDrawer,
      ...totals,
    }),
    [items, addItem, updateQuantity, removeItem, clearCart, checkout, drawerOpen, openDrawer, closeDrawer, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
